import React from 'react';
import { Popper, Fade, ClickAwayListener } from '@mui/material';
import { useForkRef } from '@mui/material/utils';
import { Box, BoxProps } from '../Box';
import { usePopoverContext } from './Popover';
import { PounceStyleProps, extractPounceProps, mapPouncePropsToSx } from '../../utils';

export type Alignment =
  | 'bottom-left'
  | 'bottom-center'
  | 'bottom-right'
  | 'top-left'
  | 'top-center'
  | 'top-right'
  | 'left-bottom'
  | 'left-center'
  | 'left-top'
  | 'right-bottom'
  | 'right-center'
  | 'right-top';

type PopperPlacement =
  | 'bottom-start'
  | 'bottom'
  | 'bottom-end'
  | 'top-start'
  | 'top'
  | 'top-end'
  | 'left-start'
  | 'left'
  | 'left-end'
  | 'right-start'
  | 'right'
  | 'right-end';

// Convert pounce alignment to Popper placement
// Note: Pounce alignment names refer to which side of the popover aligns with the trigger
// For example, 'bottom-left' means the popover appears below with its RIGHT edge aligned to trigger's RIGHT edge
const getPlacement = (alignment: Alignment): PopperPlacement => {
  switch (alignment) {
    case 'bottom-left':
      return 'bottom-end'; // Popover's right edge aligns with trigger's right edge
    case 'bottom-center':
      return 'bottom';
    case 'bottom-right':
      return 'bottom-start'; // Popover's left edge aligns with trigger's left edge
    case 'top-left':
      return 'top-end'; // Popover's right edge aligns with trigger's right edge
    case 'top-center':
      return 'top';
    case 'top-right':
      return 'top-start'; // Popover's left edge aligns with trigger's left edge
    case 'left-top':
      return 'left-end'; // Popover's bottom edge aligns with trigger's bottom edge
    case 'left-center':
      return 'left';
    case 'left-bottom':
      return 'left-start'; // Popover's top edge aligns with trigger's top edge
    case 'right-top':
      return 'right-end'; // Popover's bottom edge aligns with trigger's bottom edge
    case 'right-center':
      return 'right';
    case 'right-bottom':
    default:
      return 'right-start'; // Popover's top edge aligns with trigger's top edge
  }
};

export interface PopoverContentProps extends Omit<BoxProps, 'children'>, PounceStyleProps {
  /**
   * The position to expand the popover
   *
   * @default bottom-left
   */
  alignment?: Alignment;

  /**
   * Whether the popover should remain open when a click outside it occurs
   */
  persistOnOutsideClicks?: boolean;

  /**
   * @ignore
   */
  children: React.ReactNode;

  /**
   * The zIndex for the popover
   *
   * @default 1300
   */
  zIndex?: number;

  /** Additional styles to apply (combined with pounce props) */
  sx?: BoxProps['sx'];
}

/**
 * PounceV2 PopoverContent component built on top of MUI Popper.
 *
 * The content container for the popover that appears when the trigger is activated.
 * Maintains the same API as the original pounce PopoverContent while leveraging
 * MUI's positioning, accessibility, and animation system. Uses Popper for proper
 * scroll tracking and viewport-relative positioning.
 */
const PopoverContent = React.forwardRef<HTMLDivElement, PopoverContentProps>(
  function PopoverContent(
    {
      alignment = 'bottom-left',
      children,
      persistOnOutsideClicks = false,
      zIndex = 1300,
      sx,
      ...rest
    },
    forwardedRef,
  ) {
    const { popoverId, isOpen, triggerRef, popoverRef, close } = usePopoverContext();
    const { pounceProps, otherProps } = extractPounceProps(rest);
    const pounceStyles = mapPouncePropsToSx(pounceProps);

    const placement = getPlacement(alignment);

    // Track when anchor element is available and force re-render
    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(triggerRef.current);

    React.useLayoutEffect(() => {
      // Update anchorEl when trigger becomes available or when popover opens
      if (triggerRef.current !== anchorEl) {
        setAnchorEl(triggerRef.current);
      }
    }, [isOpen, anchorEl, triggerRef]);

    // Compose refs using MUI's useForkRef
    const ref = useForkRef(popoverRef, forwardedRef);

    // When the popover opens, it should immediately get focus
    React.useEffect(() => {
      if (isOpen && anchorEl) {
        // Use requestAnimationFrame to ensure the element is rendered
        const rafId = requestAnimationFrame(() => {
          anchorEl?.focus();
        });
        return () => cancelAnimationFrame(rafId);
      }
      return undefined;
    }, [isOpen, anchorEl]);

    // Handle click away
    const handleClickAway = (event: MouseEvent | TouchEvent) => {
      if (persistOnOutsideClicks) {
        return;
      }
      // Don't close if clicking on trigger
      if (anchorEl?.contains(event.target as Node)) {
        return;
      }
      close();
    };

    // Handle ESC key (global listener, unlike useEscapeKey which checks containment)
    React.useEffect(() => {
      if (!isOpen || persistOnOutsideClicks) {
        return undefined;
      }

      const handleEscape = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          close();
        }
      };

      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('keydown', handleEscape);
      };
    }, [isOpen, persistOnOutsideClicks, close]);

    const combinedSx = {
      ...pounceStyles,
      ...sx,
    };

    // Don't render Popper until we have a valid anchor element
    if (!anchorEl) {
      return null;
    }

    // Determine appropriate fallback placements based on primary placement direction
    const getFallbackPlacements = (): PopperPlacement[] => {
      if (placement.startsWith('bottom')) {
        return ['bottom-start', 'bottom-end', 'top-start', 'top-end', 'top'];
      }
      if (placement.startsWith('top')) {
        return ['top-start', 'top-end', 'bottom-start', 'bottom-end', 'bottom'];
      }
      if (placement.startsWith('left')) {
        return ['left-start', 'left-end', 'right-start', 'right-end', 'right'];
      }
      if (placement.startsWith('right')) {
        return ['right-start', 'right-end', 'left-start', 'left-end', 'left'];
      }
      return [];
    };

    return (
      <Popper
        open={isOpen}
        anchorEl={anchorEl}
        placement={placement}
        transition
        disablePortal={false}
        sx={{ zIndex }}
        popperOptions={{
          strategy: 'fixed',
        }}
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 0],
            },
          },
          {
            name: 'preventOverflow',
            options: {
              boundary: 'viewport',
              padding: 8,
            },
          },
          {
            name: 'flip',
            options: {
              fallbackPlacements: getFallbackPlacements(),
            },
          },
        ]}
      >
        {({ TransitionProps }) => {
          const popperContent = (
            <Fade {...TransitionProps} timeout={300}>
              <Box
                ref={ref}
                tabIndex={0}
                role="tooltip"
                id={popoverId}
                sx={{
                  outline: 'none',
                  zIndex,
                  ...combinedSx,
                }}
                {...otherProps}
              >
                {children}
              </Box>
            </Fade>
          );

          return persistOnOutsideClicks ? (
            popperContent
          ) : (
            <ClickAwayListener
              onClickAway={handleClickAway}
              mouseEvent="onMouseDown"
              touchEvent="onTouchStart"
            >
              {popperContent}
            </ClickAwayListener>
          );
        }}
      </Popper>
    );
  },
);

export default PopoverContent;
